import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getQueryClient} from '@github-ui/query-client'
import {
  useCachePreheatQueries,
  useCachePreheatQuery,
  useCachePreheatTask,
  useCachePreheatTasks,
} from '@github-ui/relay-query-cache/hooks/use-cache-preheat-query'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useMemo} from 'react'

import {issueViewerSecondaryViewQueryOptions} from '../components/fetch-issue-viewer-secondary-view'
import {IssueViewerViewGraphqlQuery, preheatIssueViewerSecondaryComponents} from '../components/IssueViewer'
import {IssueViewerSecondaryGraphqlQuery} from '../components/IssueViewerSecondaryView'

type UseCachePreheatIssueViewerProps = {
  owner: string
  repo: string
  number: number
  source: string
  allowedOwner?: string
  enabled?: boolean
}

type UseCachePreheatIssueViewerQueriesProps = {
  issues: Array<{
    owner: string
    repo: string
    number: number
  }>
  source: string
}

export function useCachePreheatIssueViewer({
  owner,
  repo,
  number,
  source,
  allowedOwner,
  enabled = true,
}: UseCachePreheatIssueViewerProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const excludeFromSecondaryQueryCacheKey = useMemo(() => ['markAsRead'], [])
  const excludeFromPrimaryQueryCacheKey = useMemo(() => ['allowedOwner'], [])

  const primaryVariables = useMemo(
    () => ({
      owner,
      repo,
      number,
      count: 15,
      allowedOwner,
    }),
    [owner, repo, number, allowedOwner],
  )

  const secondaryVariables = useMemo(
    () => ({
      owner,
      repo,
      number,
      markAsRead: false,
    }),
    [owner, repo, number],
  )

  const tanstackSecondaryViewEnabled = isFeatureEnabled('issue_viewer_secondary_view_tanstack')
  const secondaryQueryOptions = useMemo(
    () =>
      issueViewerSecondaryViewQueryOptions({
        owner,
        repo,
        number,
        markAsRead: false,
        sendAnalyticsEvent,
        preheat: true,
      }),
    [number, owner, repo, sendAnalyticsEvent],
  )
  const preheatSecondaryQuery = useCallback(
    () => getQueryClient().prefetchQuery(secondaryQueryOptions),
    [secondaryQueryOptions],
  )

  useEffect(() => {
    if (!enabled || !tanstackSecondaryViewEnabled) return
    void preheatIssueViewerSecondaryComponents()
  }, [enabled, tanstackSecondaryViewEnabled])

  useCachePreheatQuery({
    query: IssueViewerViewGraphqlQuery,
    variables: primaryVariables,
    source,
    excludeFromCacheKey: excludeFromPrimaryQueryCacheKey,
    highPriority: true,
    enabled,
  })

  useCachePreheatQuery({
    query: IssueViewerSecondaryGraphqlQuery,
    variables: secondaryVariables,
    source,
    excludeFromCacheKey: excludeFromSecondaryQueryCacheKey,
    // The TanStack task below replaces this Relay preheat when the migrated query is enabled.
    enabled: enabled && !tanstackSecondaryViewEnabled,
  })

  useCachePreheatTask({
    key: JSON.stringify(secondaryQueryOptions.queryKey),
    task: preheatSecondaryQuery,
    enabled: enabled && tanstackSecondaryViewEnabled,
  })
}

export function useCachePreheatIssueViewerQueries({issues, source}: UseCachePreheatIssueViewerQueriesProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const excludeFromSecondaryQueryCacheKey = useMemo(() => ['markAsRead'], [])
  const excludeFromPrimaryQueryCacheKey = useMemo(() => ['allowedOwner'], [])

  const queries = useMemo(() => {
    const primaryQueries = issues.map(({owner, repo, number}) => ({
      query: IssueViewerViewGraphqlQuery,
      variables: {
        owner,
        repo,
        number,
        count: 15,
      },
      source,
      excludeFromCacheKey: excludeFromPrimaryQueryCacheKey,
      highPriority: true,
    }))

    const secondaryQueries = issues.map(({owner, repo, number}) => ({
      query: IssueViewerSecondaryGraphqlQuery,
      variables: {
        owner,
        repo,
        number,
        markAsRead: false,
      },
      source,
      excludeFromCacheKey: excludeFromSecondaryQueryCacheKey,
    }))

    return [...primaryQueries, ...secondaryQueries]
  }, [issues, source, excludeFromPrimaryQueryCacheKey, excludeFromSecondaryQueryCacheKey])

  const tanstackSecondaryViewEnabled = isFeatureEnabled('issue_viewer_secondary_view_tanstack')

  useEffect(() => {
    if (!tanstackSecondaryViewEnabled || issues.length === 0) return
    void preheatIssueViewerSecondaryComponents()
  }, [issues.length, tanstackSecondaryViewEnabled])

  const preheatQueries = useMemo(
    () => (tanstackSecondaryViewEnabled ? queries.filter(q => q.query !== IssueViewerSecondaryGraphqlQuery) : queries),
    [tanstackSecondaryViewEnabled, queries],
  )

  const secondaryPreheatTasks = useMemo(
    () =>
      tanstackSecondaryViewEnabled
        ? issues.map(({owner, repo, number}) => {
            const options = issueViewerSecondaryViewQueryOptions({
              owner,
              repo,
              number,
              markAsRead: false,
              sendAnalyticsEvent,
              preheat: true,
            })
            return {
              key: JSON.stringify(options.queryKey),
              task: () => getQueryClient().prefetchQuery(options),
            }
          })
        : [],
    [issues, sendAnalyticsEvent, tanstackSecondaryViewEnabled],
  )

  useCachePreheatQueries({queries: preheatQueries})
  useCachePreheatTasks({tasks: secondaryPreheatTasks})
}
